import { Bar } from 'react-chartjs-2';
import {
  ICurrentApplicationData,
  IOrderBarProps,
} from '../ApplicationsBarChart';
import { applicationChartOptions } from '../applicationChartOptions';
import { useTranslation } from 'react-i18next';

interface GroupedDataItem {
  data: ICurrentApplicationData[];
  totalStats: {
    created: number;
    in_progress: number;
    completed: number;
    closed: number;
    expired: number;
  };
}

interface IGeneralApplicationBarProps {
  orderData: IOrderBarProps | undefined;
  setSelectedData: (label: string) => void;
  setCurrentData: (data: ICurrentApplicationData[]) => void;
}

export const GeneralApplicationBar = ({
  orderData,
  setSelectedData,
  setCurrentData,
}: IGeneralApplicationBarProps) => {
  const { t } = useTranslation();

  const groupedDataArray = Object.entries(orderData?.statistic || {}).reduce(
    (acc: Record<string, GroupedDataItem>, [key, value]) => {
      const prefix = key.split(' ')[0];

      if (!acc[prefix]) {
        acc[prefix] = {
          data: [],
          totalStats: {
            created: 0,
            in_progress: 0,
            completed: 0,
            closed: 0,
            expired: 0,
          },
        };
      }

      acc[prefix].totalStats.created += value.statuses.created;
      acc[prefix].totalStats.in_progress += value.statuses.in_progress;
      acc[prefix].totalStats.completed += value.statuses.completed;
      acc[prefix].totalStats.closed += value.statuses.closed;
      acc[prefix].totalStats.expired += value.statuses.expired;

      acc[prefix].data.push({
        label: key,
        data: {
          all_count: value.all_count,
          statistic: value.statuses,
        },
      });

      return acc;
    },
    {}
  );

  const groupedDataEntries: [string, GroupedDataItem][] =
    Object.entries(groupedDataArray);

  const sortedGroupedData = groupedDataEntries.sort((a, b) => {
    const aTotal =
      a[1].totalStats.created +
      a[1].totalStats.in_progress +
      a[1].totalStats.completed +
      a[1].totalStats.closed +
      a[1].totalStats.expired;
    const bTotal =
      b[1].totalStats.created +
      b[1].totalStats.in_progress +
      b[1].totalStats.completed +
      b[1].totalStats.closed +
      b[1].totalStats.expired;
    return bTotal - aTotal;
  });

  const stackedData = {
    labels: sortedGroupedData.map(([prefix]) => prefix),
    datasets: [
      {
        label: t('cancelled'),
        data: sortedGroupedData.map(([, data]) => data.totalStats.expired),
        backgroundColor: 'rgba(255, 188, 188, 1)',
        hoverBackgroundColor: 'rgba(255, 99, 132, 1)',
        borderRadius: 8,
        barPercentage: 0.7,
      },
      {
        label: t('created_by'),
        data: sortedGroupedData.map(([, data]) => data.totalStats.created),
        backgroundColor: 'rgba(142, 193, 255, 1)',
        hoverBackgroundColor: 'rgba(54, 162, 235, 1)',
        borderRadius: 8,
        barPercentage: 0.7,
      },
      {
        label: t('completed'),
        data: sortedGroupedData.map(([, data]) => data.totalStats.completed),
        backgroundColor: 'rgba(169, 233, 197, 1)',
        hoverBackgroundColor: 'rgba(75, 192, 192, 1)',
        borderRadius: 8,
        barPercentage: 0.7,
      },
    ],
  };

  const compatibleData: [
    string,
    {
      all_count: number;
      statuses: {
        created: number;
        in_progress: number;
        completed: number;
        closed: number;
        expired: number;
      };
    },
  ][] = sortedGroupedData.map(([prefix, data]) => [
    prefix,
    {
      all_count:
        data.totalStats.created +
        data.totalStats.in_progress +
        data.totalStats.completed +
        data.totalStats.closed +
        data.totalStats.expired,
      statuses: {
        ...data.totalStats,
      },
    },
  ]);

  const handleClick = (
    _: any,
    elements: { datasetIndex: number; index: number }[]
  ) => {
    if (elements.length > 0) {
      const element = elements[0];
      const dataIndex = element.index;
      const label = stackedData.labels[dataIndex];
      const current = sortedGroupedData.find(item => item[0] === label);
      const currentData = current
        ? (current[1].data as ICurrentApplicationData[])
        : [];

      setSelectedData(label);
      setCurrentData(currentData);
    }
  };

  const options = {
    ...applicationChartOptions(compatibleData),
    onClick: handleClick,
  };

  return <Bar data={stackedData} options={options} />;
};
